import styled, { keyframes } from "styled-components";
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { ProductContext } from "../../context/ProductContext";

const fadeScaleIn = keyframes`
  from { opacity: 0; transform: translateY(-6px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

// Iznad --zindex-top (9999, glavni sticky header) - ShopSort nije ugnježden
// unutar Header-a, pa mora imati viši z-index da ne bi iskočio ispod njega.
const OVERLAY_Z = 10001;

const RelativeAnchor = styled.div`
  position: relative;
  display: inline-block;
`;

const DesktopClickCatcher = styled.div`
  display: none;
  @media (min-width: 1040px) {
    display: ${(props) => (props.$isOpen ? "block" : "none")};
    position: fixed;
    inset: 0;
    background: transparent;
    z-index: ${OVERLAY_Z - 1};
  }
`;

const Trigger = styled.button`
  font-size: var(--font-size-base);
  font-weight: 500;
  text-transform: capitalize;
  color: var(--text-100);
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  white-space: nowrap;

  background-color: var(--bg-200);
  border-radius: var(--border-radius-base);
  padding: var(--spacing-xs) var(--spacing-md);
  border: 1px solid var(--bg-300);
  transition: border-color 0.15s ease-in-out;

  &:hover {
    border-color: var(--primary-200);
  }
`;

const ChevronIcon = styled.svg`
  flex-shrink: 0;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0)")};
`;

const Backdrop = styled.div`
  display: ${(props) => (props.$isOpen ? "block" : "none")};
  position: fixed;
  inset: 0;
  background-color: rgba(15, 17, 20, 0.55);
  z-index: ${OVERLAY_Z};

  @media (min-width: 1040px) {
    display: none;
  }
`;

const OptionsContainer = styled.div`
  background-color: var(--bg-100);
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: ${OVERLAY_Z};
  width: 100%;
  border-radius: var(--border-radius-large) var(--border-radius-large) 0 0;
  box-shadow: var(--shadow-large);
  display: ${(props) => (props.$isOpen ? "block" : "none")};
  overflow: hidden;

  @media (min-width: 1040px) {
    position: absolute;
    width: 240px;
    top: calc(100% + var(--spacing-sm));
    bottom: auto;
    right: 0;
    border-radius: var(--border-radius-large);
    animation: ${fadeScaleIn} 0.18s ease-out both;
  }
`;

const Option = styled.button`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  text-transform: capitalize;
  white-space: nowrap;
  background: none;
  border: none;
  color: var(--text-100);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  border-bottom: 1px solid var(--bg-200);
  transition: background-color 0.15s ease-in-out;

  &:last-of-type {
    border-bottom: none;
  }
  &:hover,
  &:focus-visible {
    background-color: var(--bg-200);
  }
`;

const OptionLabel = styled.span`
  flex: 1;
  text-align: left;
`;

const DirectionIcon = styled.svg`
  flex-shrink: 0;
  color: var(--text-200);
`;

const CheckDot = styled.div`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid
    ${(props) => (props.$checked ? "var(--primary-100)" : "var(--bg-300)")};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const CheckDotInner = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--primary-100);
`;

// Strelica na gore/dole daje vizuelni znak smera sortiranja za parove kao
// "cena rastuće/opadajuće", umesto da oba izgledaju identično sa samo
// tekstom kao jedinom razlikom.
const ArrowUp = () => (
  <DirectionIcon width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 19V5M12 5l-6 6M12 5l6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </DirectionIcon>
);
const ArrowDown = () => (
  <DirectionIcon width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 5v14M12 19l-6-6M12 19l6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </DirectionIcon>
);
const DIRECTION_ICONS = {
  price_asc: ArrowUp,
  price_desc: ArrowDown,
  strength: ArrowUp,
  strength_desc: ArrowDown,
};

const OpenSortTitle = styled.div`
  text-transform: uppercase;
  font-weight: 600;
  font-size: var(--font-size-small);
  color: var(--text-100);
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--bg-300);
  @media (min-width: 1040px) {
    display: none;
  }
`;

const ShopSort = () => {
  const { t } = useTranslation();
  const items = [
    "date",
    "strength",
    "strength_desc",
    "price_asc",
    "price_desc",
    "name",
    "best_sellers",
  ];
  const [selectedSort, setSelectedSort] = useState("date");
  let { sortProducts, products } = useContext(ProductContext);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (selectedSort === "price_desc") {
      sortProducts(products, "price", false);
    } else if (selectedSort === "price_asc") {
      sortProducts(products, "price", true);
    } else if (selectedSort === "strength_desc") {
      sortProducts(products, "strength", false);
    } else {
      sortProducts(products, selectedSort);
    }
  }, [selectedSort]);

  const toggleSort = () => {
    setIsOpen(!isOpen);
  };

  return (
    <RelativeAnchor>
      <Trigger onClick={toggleSort} aria-expanded={isOpen}>
        {t("SHOP_SORT.SORT")}
        <ChevronIcon
          $isOpen={isOpen}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="var(--text-200)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </ChevronIcon>
      </Trigger>

      <DesktopClickCatcher $isOpen={isOpen} onClick={toggleSort} />
      <Backdrop $isOpen={isOpen} onClick={toggleSort} />

      <OptionsContainer $isOpen={isOpen}>
        <OpenSortTitle>{t("SHOP_SORT.SORT")}</OpenSortTitle>
        {items.map((item, index) => {
          const checked = item === selectedSort;
          const DirectionArrow = DIRECTION_ICONS[item];
          return (
            <Option
              key={index}
              onClick={() => {
                setSelectedSort(item);
                setIsOpen(false);
              }}
            >
              {DirectionArrow && <DirectionArrow />}
              <OptionLabel>{t(`SHOP_SORT.${item.toUpperCase()}`)}</OptionLabel>
              <CheckDot $checked={checked}>
                {checked && <CheckDotInner />}
              </CheckDot>
            </Option>
          );
        })}
      </OptionsContainer>
    </RelativeAnchor>
  );
};

export default ShopSort;
